import { PayloadAction } from '@reduxjs/toolkit';
import { CancelToken } from 'axios';
import { call, put, select } from 'redux-saga/effects';

import { $httpRootApi, getCancelRequestSource } from '@core/http-client';
import { configActions } from '@redux/reducers/config/configReducer';
import { savedViewsCampaignsActions } from '@redux/reducers/saved_views/campaigns/campaignsReducer';
import { savedViewsCampaignsSelectors } from '@redux/selectors/saved_views/campaigns/savedViewCampaigns';
import {
  ManageSavedViewRequestPayload,
  PinUnpinSavedViewRequestPayload,
  SavedViewState,
  UpdateSavedViewRequestPayload,
} from '@redux/types/saved-views/common/savedViewsCommonTypes';
import { showNotification } from '@shared/components/notification';
import { getDefaultError } from '@shared/utils/common';

const getSaveViews = () => {
  return $httpRootApi.get(`/saved-views/email-campaigns`);
};

const getSavedViewById = (id: Id) => {
  return $httpRootApi.get(`/saved-views/email-campaigns/${id}`);
};

const updatePinnedSavedViews = ({ userId, pinnedIds }: UpdateSavedViewRequestPayload, cancelToken: CancelToken) => {
  return $httpRootApi.put(
    `/users/${userId}/update/pinned_views_email_campaigns`,
    {
      pinned_views_email_campaigns: pinnedIds,
    },
    { cancelToken }
  );
};

const deleteSavedView = (id: Id) => {
  return $httpRootApi.delete(`/saved-views/${id}`);
};

const editSavedView = (data: ManageSavedViewRequestPayload) => {
  return $httpRootApi.put(`/saved-views/${data.id}`, data);
};

const createSavedView = (data: ManageSavedViewRequestPayload) => {
  return $httpRootApi.post(`/saved-views/email-campaigns`, data);
};

export function* getCampaignsSavedViewsSaga() {
  try {
    yield put(savedViewsCampaignsActions.setListLoading(true));

    const { data } = yield call(getSaveViews);

    yield put(savedViewsCampaignsActions.getListSuccess(data.data));
  } catch (e) {
    const errorMessage = getDefaultError(e?.repsponse?.data?.message);

    showNotification(errorMessage, 'error');
    yield put(savedViewsCampaignsActions.getListFailure(errorMessage));
  }
}

export function* getCampaignsSavedViewById(action: PayloadAction<Id>) {
  try {
    yield put(savedViewsCampaignsActions.setUnpinnedViewLoading(true));

    const { data } = yield call(() => getSavedViewById(action.payload));

    yield put(savedViewsCampaignsActions.getUnpinnedViewSuccess(data));
  } catch (e) {
    yield put(savedViewsCampaignsActions.getUnpinnedViewFailure(getDefaultError(e?.response?.data?.message)));
  }
}

export function* updatePinnedCampaignsSavedViewsSaga(action: PayloadAction<UpdateSavedViewRequestPayload>) {
  const source = getCancelRequestSource();

  try {
    yield put(savedViewsCampaignsActions.setUpdateViewsRequestLoading(true));

    const { data } = yield call(() => updatePinnedSavedViews(action.payload, source.token));

    yield put(savedViewsCampaignsActions.updateViewsSuccess());
    yield put(configActions.setUser(data));
  } catch (e) {
    yield put(savedViewsCampaignsActions.updateViewsFailure(getDefaultError(e?.response?.data?.message)));
  } finally {
    const { updateLoading }: SavedViewState = yield select(savedViewsCampaignsSelectors.getState);

    if (updateLoading) {
      source.cancel();
    }
  }
}

export function* pinSavedViewCampaignSaga(action: PayloadAction<PinUnpinSavedViewRequestPayload>) {
  const source = getCancelRequestSource();

  try {
    yield put(savedViewsCampaignsActions.setUpdateViewsRequestLoading(true));

    const { data } = yield call(() => updatePinnedSavedViews(action.payload, source.token));

    yield put(configActions.setUser(data));
    yield put(
      savedViewsCampaignsActions.pinViewSuccess({
        view: action.payload.view,
        isActiveView: action.payload.isActiveView,
      })
    );
  } finally {
    const { updateLoading }: SavedViewState = yield select(savedViewsCampaignsSelectors.getState);

    if (updateLoading) {
      source.cancel();
    }
  }
}

export function* unpinSavedViewCampaignSaga(action: PayloadAction<PinUnpinSavedViewRequestPayload>) {
  const source = getCancelRequestSource();

  try {
    yield put(savedViewsCampaignsActions.setUpdateViewsRequestLoading(true));

    const { data } = yield call(() => updatePinnedSavedViews(action.payload, source.token));

    yield put(configActions.setUser(data));
    yield put(
      savedViewsCampaignsActions.unpinViewSuccess({
        view: action.payload.view,
        isActiveView: action.payload.isActiveView,
      })
    );
  } finally {
    const { updateLoading }: SavedViewState = yield select(savedViewsCampaignsSelectors.getState);

    if (updateLoading) {
      source.cancel();
    }
  }
}

export function* deleteSavedViewCampaignsSaga(action: PayloadAction<Id>) {
  try {
    yield call(() => deleteSavedView(action.payload));

    yield put(configActions.deleteSavedViewCampaignsSuccess(action.payload));
    yield put(savedViewsCampaignsActions.deleteViewSuccess(action.payload));
  } catch (e) {
    showNotification(getDefaultError(e?.message), 'error');
  }
}

export function* editSavedViewCampaignsSaga(action: PayloadAction<ManageSavedViewRequestPayload>) {
  try {
    const { data } = yield call(() => editSavedView(action.payload));

    yield put(configActions.editSavedViewCampaignsSuccess(data));
    yield put(savedViewsCampaignsActions.editViewSuccess(data));
  } catch (e) {
    showNotification(
      getDefaultError('Sorry, we could not update your view. Please contact us for assistance.'),
      'error'
    );
  }
}

export function* createSavedViewCampaignsSaga(action: PayloadAction<ManageSavedViewRequestPayload>) {
  try {
    const { data } = yield call(() => createSavedView(action.payload));

    yield put(savedViewsCampaignsActions.createViewSuccess(data));
  } catch (e) {
    showNotification(getDefaultError(e?.message), 'error');
  }
}
